import React, { useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text } from '@react-three/drei';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Info, HelpCircle } from 'lucide-react';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import * as THREE from 'three';

interface Performance3DChartsProps {
  fandom?: number;
  trust?: number;
  referrals?: number;
  redFlags?: string[];
  brightSpots?: string[];
}

// Constants for the 3D visualization
const CHART_COLORS = {
  fandom: '#9b87f5',   // purple
  trust: '#10B981',    // green
  referrals: '#3B82F6', // blue
  background: '#E5DEFF' // light purple background
};

const METRICS_DESCRIPTION = {
  fandom: "Measures audience passion and engagement with your project. High scores indicate strong emotional connection.",
  trust: "Evaluates stakeholder trust in your project. Higher scores mean greater confidence and credibility.",
  referrals: "Reflects how likely users are to recommend your project. Higher scores indicate strong advocacy potential."
};

// A component to render a 3D bar
const Bar3D: React.FC<{
  position: [number, number, number];
  size: [number, number, number];
  color: string;
  label: string;
  value: number;
}> = ({ position, size, color, label, value }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  
  useFrame(() => {
    if (meshRef.current) {
      meshRef.current.rotation.y += 0.002;
    }
  });

  return (
    <group position={position}>
      {/* The bar */}
      <mesh ref={meshRef} position={[0, size[1]/2, 0]}>
        <boxGeometry args={size} />
        <meshStandardMaterial color={color} transparent opacity={0.8} />
      </mesh>
      
      {/* The label */}
      <Text position={[0, -0.2, 0]} scale={[0.15, 0.15, 0.15]} color="#333333">
        {label}
      </Text>
      
      {/* The value */}
      <Text position={[0, size[1] + 0.2, 0]} scale={[0.2, 0.2, 0.2]} color="#333333" anchorY="bottom">
        {value.toFixed(1)}
      </Text>
    </group>
  );
};

// The 3D scene component
const MetricsScene: React.FC<{
  fandom: number;
  trust: number;
  referrals: number;
}> = ({ fandom, trust, referrals }) => {
  // Normalize values (assuming they're on a 0-10 scale)
  const maxPossibleValue = 10;
  const barWidth = 0.6;
  const barDepth = 0.6;
  const spacing = 1.5;
  
  // Calculate positions for each bar
  const positions: [number, number, number][] = [
    [-spacing, 0, 0],   // fandom
    [0, 0, 0],          // trust
    [spacing, 0, 0]     // referrals
  ];
  
  return (
    <>
      {/* Grid helper */}
      <gridHelper args={[10, 10]} position={[0, 0, 0]} rotation={[Math.PI/2, 0, 0]} />
      
      {/* Bars */}
      <Bar3D 
        position={positions[0]}
        size={[barWidth, (fandom / maxPossibleValue) * 2, barDepth]}
        color={CHART_COLORS.fandom}
        label="Fandom"
        value={fandom}
      />
      <Bar3D 
        position={positions[1]}
        size={[barWidth, (trust / maxPossibleValue) * 2, barDepth]}
        color={CHART_COLORS.trust}
        label="Trust"
        value={trust}
      />
      <Bar3D 
        position={positions[2]}
        size={[barWidth, (referrals / maxPossibleValue) * 2, barDepth]}
        color={CHART_COLORS.referrals}
        label="Referrals"
        value={referrals}
      />

      {/* Y-axis scale markers */}
      {[0, 2.5, 5, 7.5, 10].map((value, index) => {
        const height = (value / maxPossibleValue) * 2;
        return (
          <group key={`y-scale-${index}`} position={[-2.5, height, 0]}>
            <mesh>
              <boxGeometry args={[0.1, 0.01, 0.01]} />
              <meshBasicMaterial color="#666666" />
            </mesh>
            <Text position={[-0.2, 0, 0]} scale={[0.12, 0.12, 0.12]} color="#666666">
              {value.toFixed(0)}
            </Text>
          </group>
        );
      })}
      
      {/* Explanatory text */}
      <Text position={[0, 2.5, 0]} scale={[0.2, 0.2, 0.2]} color="#333333">
        Performance Metrics (0-10 Scale)
      </Text>
      
      {/* Lighting */}
      <ambientLight intensity={0.6} />
      <directionalLight position={[5, 5, 5]} intensity={0.8} />
      <directionalLight position={[-5, 5, -5]} intensity={0.4} />
    </>
  );
};

const Performance3DCharts: React.FC<Performance3DChartsProps> = ({ 
  fandom = 0,
  trust = 0, 
  referrals = 0,
  redFlags = [],
  brightSpots = []
}) => {
  // Ensure values are within 0-10 range
  const safeFandom = Math.max(0, Math.min(10, fandom));
  const safeTrust = Math.max(0, Math.min(10, trust));
  const safeReferrals = Math.max(0, Math.min(10, referrals));

  const getRatingText = (score: number): string => {
    if (score < 3.5) return "Needs Improvement";
    if (score < 6.5) return "Moderate";
    if (score < 8.5) return "Good";
    return "Excellent";
  };
  
  const getRatingColor = (score: number): string => {
    if (score < 3.5) return "text-red-600";
    if (score < 6.5) return "text-amber-500";
    if (score < 8.5) return "text-emerald-600";
    return "text-green-600";
  };

  return (
    <div className="space-y-8">
      <Card className="shadow-md">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center">
            3D Performance Analysis
            <Popover>
              <PopoverTrigger asChild>
                <button className="ml-1 text-gray-400 hover:text-gray-600">
                  <Info className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-4">
                <h4 className="font-medium mb-2">Understanding Performance Metrics</h4>
                <p className="text-sm text-gray-600 mb-3">
                  This 3D visualization displays three key performance metrics that show your project's emotional connection:
                </p>
                <ul className="text-xs text-gray-600 space-y-1 mb-3">
                  <li><span className="font-medium">Fandom:</span> {METRICS_DESCRIPTION.fandom}</li>
                  <li><span className="font-medium">Trust:</span> {METRICS_DESCRIPTION.trust}</li>
                  <li><span className="font-medium">Referrals:</span> {METRICS_DESCRIPTION.referrals}</li>
                </ul>
                <p className="text-xs text-gray-600">
                  <span className="font-medium">Interact:</span> Click and drag to rotate the view. Use mouse wheel to zoom in/out.
                </p>
              </PopoverContent>
            </Popover>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] w-full bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg overflow-hidden mb-5">
            <Canvas camera={{ position: [0, 2, 5], fov: 45 }}>
              <MetricsScene 
                fandom={safeFandom} 
                trust={safeTrust} 
                referrals={safeReferrals} 
              />
              <OrbitControls
                enableZoom={true}
                enablePan={true}
                enableRotate={true}
                minDistance={3}
                maxDistance={10}
              />
            </Canvas>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Fandom", value: safeFandom, color: CHART_COLORS.fandom, description: METRICS_DESCRIPTION.fandom },
              { label: "Trust", value: safeTrust, color: CHART_COLORS.trust, description: METRICS_DESCRIPTION.trust },
              { label: "Referrals", value: safeReferrals, color: CHART_COLORS.referrals, description: METRICS_DESCRIPTION.referrals }
            ].map((metric) => (
              <div key={metric.label} className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                <div className="flex items-center mb-1 gap-1.5">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: metric.color }}
                  ></div>
                  <span className="text-sm font-medium text-gray-700">
                    {metric.label}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-xl font-semibold">
                      {metric.value.toFixed(1)}
                      <span className="text-xs font-normal text-gray-500">/10</span>
                    </div>
                    <div className={`text-xs font-medium ${getRatingColor(metric.value)}`}>
                      {getRatingText(metric.value)}
                    </div>
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="p-1 text-gray-400 hover:text-gray-600">
                          <HelpCircle className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="p-3 max-w-xs">
                        <p className="text-xs">{metric.description}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-4 pt-3 border-t border-gray-100">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Score Interpretation</h4>
            <div className="flex items-center justify-between text-xs text-gray-600">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-red-600 mr-1"></div>
                <span>0-3.4: Needs Improvement</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-amber-500 mr-1"></div>
                <span>3.5-6.4: Moderate</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-emerald-600 mr-1"></div>
                <span>6.5-8.4: Good</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-600 mr-1"></div>
                <span>8.5-10: Excellent</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Legacy Value Indicators */}
      {(redFlags.length > 0 || brightSpots.length > 0) && (
        <Card className="shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Legacy Value Indicators</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Red Flags */}
              {redFlags.length > 0 && (
                <div className="border border-white/50 bg-white/30 backdrop-blur-sm shadow-md hover:shadow-lg transition-all duration-300 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                      <div className="w-3 h-3 rounded-full bg-red-600"></div>
                    </div>
                    <h4 className="font-semibold">Risk Factors</h4>
                  </div>
                  <ul className="space-y-2">
                    {redFlags.map((flag, index) => (
                      <li key={index} className="text-sm flex items-start gap-2">
                        <span className="text-red-500 text-lg">•</span>
                        <span>{flag}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Bright Spots */}
              {brightSpots.length > 0 && (
                <div className="border border-white/50 bg-white/30 backdrop-blur-sm shadow-md hover:shadow-lg transition-all duration-300 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <div className="w-3 h-3 rounded-full bg-green-600"></div>
                    </div>
                    <h4 className="font-semibold">Strengths</h4>
                  </div>
                  <ul className="space-y-2">
                    {brightSpots.map((spot, index) => (
                      <li key={index} className="text-sm flex items-start gap-2">
                        <span className="text-green-500 text-lg">•</span>
                        <span>{spot}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Performance3DCharts;
